
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useBilling } from '@/hooks/billing/useBilling';
import { useAdminStatus } from '@/hooks/admin/useAdminStatus';
import BillingNavbar from '@/components/billing/BillingNavbar';
import { format } from 'date-fns';
import { FileText, CheckCircle } from 'lucide-react';
import { generateContractPDF } from '@/utils/pdfGenerator';

const ContractsPage: React.FC = () => {
  const { contracts, isLoading } = useBilling();
  const { isAdmin } = useAdminStatus();

  const handleGeneratePDF = (contract: any) => {
    generateContractPDF(contract);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Loading contracts...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <BillingNavbar />
      
      <div className="container mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Contracts</h1>
          <p className="text-gray-600">
            {isAdmin 
              ? 'View all signed contracts and agreements'
              : 'Your signed contracts and service agreements'
            }
          </p>
        </div>

        {contracts.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-gray-500">No contracts found</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {contracts.map((contract) => (
              <Card key={contract.id}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">
                      Contract #{contract.id.slice(-8)}
                    </CardTitle>
                    {contract.terms_accepted && (
                      <Badge className="bg-green-500">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Signed
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-gray-600">
                    Created: {format(new Date(contract.created_at), 'MMM d, yyyy')}
                  </p>
                </CardHeader>

                <CardContent>
                  <div className="space-y-4">
                    <div className="text-sm text-gray-600">
                      Estimate: #{contract.estimate_id.slice(-8)}
                    </div>

                    {contract.signed_at && (
                      <div className="text-sm text-green-600">
                        Signed: {format(new Date(contract.signed_at), 'MMM d, yyyy h:mm a')}
                      </div>
                    )}

                    {contract.signature_data?.signerName && (
                      <div className="text-sm text-gray-600">
                        Signed by: {contract.signature_data.signerName}
                      </div>
                    )}

                    <div className="flex flex-col space-y-2">
                      <Button
                        variant="outline"
                        onClick={() => handleGeneratePDF(contract)}
                        className="flex items-center"
                      >
                        <FileText className="h-4 w-4 mr-2" />
                        Download Contract
                      </Button>

                      {contract.contract_pdf_url && (
                        <Button
                          variant="outline"
                          onClick={() => window.open(contract.contract_pdf_url, '_blank')}
                          className="flex items-center"
                        >
                          <FileText className="h-4 w-4 mr-2" />
                          View Original PDF
                        </Button>
                      )}
                    </div>

                    {contract.signature_data?.signature && (
                      <div className="mt-4 p-2 border rounded">
                        <p className="text-xs text-gray-500 mb-2">Digital Signature:</p>
                        <img 
                          src={contract.signature_data.signature} 
                          alt="Digital Signature" 
                          className="max-h-16 border rounded"
                        />
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ContractsPage;
